import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Eye, ScanLine, UploadCloud, Wand2 } from "lucide-react";
import { inr, ymd } from "@/lib/format";
import { toast } from "sonner";

export default function Documents({ kind }) {
  const label = kind === "INV" ? "Invoice" : "Purchase Order";
  const [docs, setDocs] = useState([]);
  const nav = useNavigate();
  const [scanOpen, setScanOpen] = useState(false);

  const load = () => api.get("/documents", { params: { kind } }).then(({ data }) => setDocs(data));
  useEffect(() => { load(); }, [kind]);

  const newPath = kind === "INV" ? "/invoices/new" : "/purchase-orders/new";

  const statusColor = (s) => {
    if (s === "paid") return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (s === "partial") return "bg-amber-100 text-amber-700 border-amber-200";
    if (s === "cancelled") return "bg-slate-100 text-slate-500 border-slate-200";
    return "bg-indigo-100 text-indigo-700 border-indigo-200";
  };

  return (
    <div>
      <PageHeader
        title={`${label}s`}
        subtitle={kind === "INV" ? "Sales invoices to customers" : "Purchase orders to suppliers"}
        action={
          <div className="flex gap-2">
            {kind === "INV" && (
              <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="scan-reference-btn">
                <ScanLine className="w-4 h-4 mr-1" /> Scan Reference Invoice
              </Button>
            )}
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => nav(newPath)} data-testid={`new-${kind}-btn`}>
              <Plus className="w-4 h-4 mr-1" /> New {label}
            </Button>
          </div>
        }
      />
      {kind === "INV" && <ScanReferenceDialog open={scanOpen} onOpenChange={setScanOpen} onCreated={load} />}
      <div className="px-8 py-6">
        <Card className="border-slate-200 overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>{kind === "INV" ? "Customer" : "Supplier"}</TableHead>
                <TableHead>Due</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No {label.toLowerCase()}s yet.</TableCell></TableRow>
              )}
              {docs.map((d) => (
                <TableRow key={d.doc_id}>
                  <TableCell><Link to={`/documents/${d.doc_id}`} className="font-medium text-slate-900 hover:text-indigo-700">{d.number}</Link></TableCell>
                  <TableCell className="text-slate-500">{ymd(d.date)}</TableCell>
                  <TableCell>{d.party_name}</TableCell>
                  <TableCell className="text-slate-500">{ymd(d.due_date)}</TableCell>
                  <TableCell className="text-right number-cell font-semibold">{inr(d.total)}</TableCell>
                  <TableCell className="text-right number-cell">{inr(d.amount_paid || 0)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`uppercase text-[10px] tracking-wider ${statusColor(d.status)}`}>
                      {d.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Link to={`/documents/${d.doc_id}`}>
                      <Button variant="ghost" size="icon"><Eye className="w-4 h-4" /></Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}

function ScanReferenceDialog({ open, onOpenChange, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [file, setFile] = useState(null);
  const [priceReduction, setPriceReduction] = useState(100);
  const [maxValue, setMaxValue] = useState(100000);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/parties", { params: { kind: "customer" } }).then(({ data }) => {
        setCustomers(data);
        const asmita = data.find((p) => /asmita/i.test(p.name));
        if (asmita) setCustomerId(asmita.party_id);
      });
    } else {
      setFile(null); setCustomerId(""); setPriceReduction(100); setMaxValue(100000);
    }
  }, [open]);

  const run = async () => {
    if (!file) return toast.error("Choose a reference invoice PDF/image");
    if (!customerId) return toast.error("Select a customer");
    setBusy(true);
    try {
      // 1. upload
      const fd = new FormData(); fd.append("file", file);
      const { data: up } = await api.post("/bills/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // 2. from-scan
      const { data } = await api.post("/invoices/from-scan", {
        file_id: up.file_id, customer_id: customerId,
        price_reduction: Number(priceReduction) || 0,
        max_invoice_value: Number(maxValue) || 100000,
      });
      toast.success(`Created ${data.count} invoice${data.count !== 1 ? "s" : ""} — total ₹${data.invoices.reduce((s, x) => s + x.total, 0).toFixed(2)}`);
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-indigo-600" /> Scan Reference Invoice
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-slate-600">
            Upload a supplier's invoice to a customer. We'll extract the parts, apply your rules, and auto-create one or more invoices to the chosen customer.
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="scan-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => (<SelectItem key={c.party_id} value={c.party_id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Reference invoice (PDF/JPG/PNG)</Label>
            <div
              onClick={() => document.getElementById("scan-ref-file")?.click()}
              className="mt-1 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40"
            >
              <UploadCloud className="w-6 h-6 mx-auto text-slate-400" />
              <div className="mt-1 text-sm">{file ? file.name : "Click to choose file"}</div>
            </div>
            <input
              id="scan-ref-file"
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="scan-ref-file"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Rate reduction per unit (₹)</Label>
              <Input type="number" value={priceReduction} onChange={(e) => setPriceReduction(e.target.value)} data-testid="price-reduction" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Max invoice value (₹)</Label>
              <Input type="number" value={maxValue} onChange={(e) => setMaxValue(e.target.value)} data-testid="max-value" />
            </div>
          </div>
          <div className="text-xs text-slate-500">
            Rate on our invoice = source rate − reduction. If total goes above the cap, quantities are auto-split into multiple invoices.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={run}
            disabled={busy}
            data-testid="scan-run-btn"
          >
            {busy ? (
              <><Wand2 className="w-4 h-4 mr-1 animate-pulse" /> Reading & creating…</>
            ) : (
              <><Wand2 className="w-4 h-4 mr-1" /> Auto-create Invoices</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
